import { IconDefinition, faArrowRight, faCheckDouble, faExclamationTriangle, faGear, faInfoCircle, faMailboxFlagUp, faQuestion, faRectangleHistory } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Badge, Divider, IconButton, Popover, Stack, Tooltip, Typography } from '@vapor/material'
import { Fragment, MouseEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HicIconButton } from '../hic-vapor/button/icon-button'
import { NotificationBellCrud } from './notification-bell.crud'
import { useProperty } from 'src/app/hooks/context/context.hook'
import ConfigureNotification from './components/configure-notification'
import { MAX_DAYS_NOTIFICATION, NotificationBellType, NotificationType } from './notification-bell.type'
import { HiCIcon } from '../icons/icon'
import { formatDistance } from 'date-fns'
import useCurrentLocale from 'src/app/hooks/current-locale/use-current-locale'
import { faCircle } from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'
import { compareDates } from 'src/utils/date'
import NotificationsIcon from '@mui/icons-material/Notifications'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import './style.css'
import BellLoading from './components/bell-loading'
import parse from 'html-react-parser'

export default function NotificationBell() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { sm } = useBreakpoints()
  const property = useProperty()
  const tenantId = property?.tenantId ?? ''
  const propertyId = property?.id ?? ''
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [showConfiguration, setShowConfiguration] = useState(false)
  const open = Boolean(anchorEl)
  const id = open ? 'notification-popover' : undefined

  const { loading, notificationUnread, notifications, userId, setReadNotification, setAllReadNotification } = NotificationBellCrud({
    propertyId,
    tenantId,
    open,
  })

  const otherNotifications = notifications.filter((notification) => notification.type !== NotificationType.MESSAGE && Boolean(notification.notification?.title))
  const messagesNotifications = notifications.filter((notification) => notification.type === NotificationType.MESSAGE && Boolean(notification.notification?.title) && !notification.read)
  const mostRecentMessage = messagesNotifications.sort((firstMessage, secondMessage) => {
    if (!firstMessage.createdDate || !secondMessage.createdDate) return 0

    return compareDates(new Date(secondMessage.createdDate), new Date(firstMessage.createdDate), true)
  })[0]

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
    setShowConfiguration(false)
  }, [])

  async function setRead(notification: NotificationBellType) {
    if (!notification.id) return
    if (notification.read) return

    return await setReadNotification({
      variables: {
        notificationId: notification.id,
        read: true,
      },
    })
  }

  return (
    <>
      <IconButton onClick={handleClick}>
        <Badge badgeContent={notificationUnread} max={99} sx={{ '& .MuiBadge-badge': { m: '3px' } }}>
          <NotificationsIcon id="notification-bell" />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Stack
          padding={2}
          sx={{
            boxSizing: 'border-box',
            width: sm ? 450 : 300,
            minHeight: 400,
            maxHeight: 650,
          }}
        >
          {loading ? (
            <BellLoading />
          ) : (
            <>
              {showConfiguration ? (
                <ConfigureNotification
                  userId={userId}
                  onBack={() => {
                    setShowConfiguration(false)
                  }}
                />
              ) : (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="titleSmall">{t('pms.notifications.title')}</Typography>
                    <Stack direction="row" gap={1}>
                      <Tooltip title={t('pms.notifications.tooltip.markAllRead')}>
                        <HicIconButton
                          sx={{ minWidth: '0px!important', minHeight: '0px!important', padding: 1 }}
                          onClick={() => {
                            setAllReadNotification({
                              variables: {
                                tenantId,
                                propertyId,
                                userId,
                                read: true,
                              },
                            })
                          }}
                        >
                          <FontAwesomeIcon icon={faCheckDouble} />
                        </HicIconButton>
                      </Tooltip>
                      <Tooltip title={t('pms.notifications.tooltip.configure')}>
                        <HicIconButton
                          sx={{ minWidth: '0px!important', minHeight: '0px!important', padding: 1 }}
                          onClick={() => {
                            setShowConfiguration(true)
                          }}
                        >
                          <FontAwesomeIcon icon={faGear} size="lg" />
                        </HicIconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Divider sx={{ marginY: 2 }} />
                  <Stack gap={2}>
                    {mostRecentMessage && !mostRecentMessage.read && (
                      <>
                        <NotificationRow
                          createdDate={mostRecentMessage.createdDate ? new Date(mostRecentMessage.createdDate) : null}
                          icon={faMailboxFlagUp}
                          title={t('pms.notifications.messages')}
                          message={t('pms.notifications.messages.description')}
                          read={mostRecentMessage.read ?? false}
                          onClick={() => {
                            setRead(mostRecentMessage)
                          }}
                          onNext={() => {
                            navigate('/messages')
                            handleClose()
                          }}
                        />
                        <Divider flexItem />
                      </>
                    )}
                    {otherNotifications.map((notification) => {
                      if (!notification.notification) return null

                      return (
                        <Fragment key={notification.id}>
                          <NotificationRow
                            createdDate={notification.createdDate ? new Date(notification.createdDate) : null}
                            icon={getNotificationIcon(notification.type)}
                            message={notification.notification?.message ?? ''}
                            title={notification.notification?.title ?? ''}
                            read={notification.read ?? false}
                            onClick={() => {
                              setRead(notification)
                            }}
                            onNext={
                              notification.type === NotificationType.RESERVATION
                                ? () => {
                                    navigate('/booking/reservations/LIST')
                                    handleClose()
                                  }
                                : undefined
                            }
                          />
                          <Divider flexItem />
                        </Fragment>
                      )
                    })}
                    <Typography variant="caption" color="var(--QuickSilver)" textAlign="center" marginBottom={2}>
                      {t('pms.notifications.footer', { days: MAX_DAYS_NOTIFICATION })}
                    </Typography>
                  </Stack>
                </>
              )}
            </>
          )}
        </Stack>
      </Popover>
    </>
  )
}

function getNotificationIcon(type: NotificationType): IconDefinition {
  switch (type) {
    case NotificationType.INFO:
      return faInfoCircle
    case NotificationType.ALERT:
      return faExclamationTriangle
    case NotificationType.RESERVATION:
      return faRectangleHistory
    case NotificationType.MESSAGE:
      return faMailboxFlagUp
    default:
      return faQuestion
  }
}

function NotificationRow({
  createdDate,
  icon,
  title,
  message,
  read,
  onClick,
  onNext,
}: {
  icon: IconDefinition
  title: string
  message: string
  createdDate: Date | null
  read: boolean
  onClick: () => void
  onNext?: () => void
}) {
  const { dateFnsLocale } = useCurrentLocale()

  return (
    <Stack direction="row" gap={1} alignItems="start" justifyContent="space-between" sx={{ cursor: 'pointer' }} onClick={onClick}>
      <Stack direction="row" gap={1} alignItems="start">
        <HiCIcon sx={{ padding: '10px', paddingLeft: 0 }}>
          <FontAwesomeIcon icon={icon} size="xl" />
        </HiCIcon>
        <Stack>
          <Typography variant="titleXSmall">{parse(title)}</Typography>
          <Typography variant="body">{parse(message)}</Typography>
          {createdDate && (
            <Typography variant="caption" marginTop={1.5}>
              {formatDistance(createdDate, new Date(), { locale: dateFnsLocale, includeSeconds: true, addSuffix: true })}
            </Typography>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center">
        {!read && <FontAwesomeIcon icon={faCircle} color="var(--error)" size="2xs" />}
        <Stack width={32} height={40}>
          {onNext && (
            <HicIconButton onClick={onNext} sx={{ minWidth: '0px!important', minHeight: '0px!important', padding: 1 }}>
              <FontAwesomeIcon icon={faArrowRight} />
            </HicIconButton>
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}
